import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';

// Server: moderation/ModerationService.java's own closed `REASONS`/`SUBJECT_KINDS` sets — the
// Report sheet's radio list must submit one of these exact strings (server 400 `validation`
// "Unknown report reason."/"Unknown subject kind." otherwise). Kept in sync by hand (no content-
// driven endpoint exposes this enum); SCREENS.md's own "Report sheet" row lists the same 5 reasons
// verbatim.
export const REPORT_REASONS = [
  'inappropriate_name',
  'inappropriate_content',
  'harassment',
  'spam',
  'other',
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

// QA 2026-09-23 (social F21): the ONE human label per reason code. Used by the Report sheet's
// radios, the staff queue's Reason column/select, and every owner-facing "Hidden pending review"
// notice (Looks sheet, own profile, guild home) — none of which should print `inappropriate_name`
// at a person. An unknown code (a future reason the server adds first) humanises rather than
// leaking the raw snake_case.
const REASON_LABELS: Record<string, string> = {
  inappropriate_name: 'Inappropriate name',
  inappropriate_content: 'Inappropriate content',
  harassment: 'Harassment',
  spam: 'Spam',
  other: 'Other',
};
export function reasonLabel(code: string | null | undefined): string {
  if (!code) return '';
  return REASON_LABELS[code] ?? code.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export const REPORT_SUBJECT_KINDS = ['look', 'profile', 'message', 'guild', 'stall'] as const;
export type ReportSubjectKind = (typeof REPORT_SUBJECT_KINDS)[number];

// `subjectId` (a raw look/message/guild id), `subjectHandle` (a unique account handle, "profile"
// kind only), and `subjectCode` (an opaque stall code, "stall" kind only) are mutually exclusive
// per report — a caller supplies exactly one, matching server:
// moderation/ReportController.java's `ReportRequest` record field-for-field. All three optional on
// the TS type (rather than a discriminated union keyed on `subjectKind`) since nothing here
// re-validates which one a given kind actually wants; the server is still the real authority
// (unknown/mismatched combinations just resolve to the usual opaque not_found below).
export type SubmitReportInput = {
  subjectKind: ReportSubjectKind;
  subjectId?: string;
  subjectHandle?: string;
  subjectCode?: string;
  reason: ReportReason;
  detailPhraseCode?: string;
};

// POST /reports — ReportController, void response, never flag-gated (any authenticated account
// may report anything). Refusals: validation (400, ReportInvalidException — unknown subject
// kind/reason/detail phrase code). Two cases never surface as an error at all: a self-report is a
// silent no-op (200, nothing filed), and a duplicate report from the same account against the same
// subject is silently deduped server-side (an `ON CONFLICT DO NOTHING` insert) — both still return
// a plain 200 here, so the client can always show "Report submitted" without special-casing them.
// Also: rate_limited (429, 10/day), not_found (404, ModerationSubjectNotFoundException — the
// subject id/handle doesn't resolve for its kind; see that class's own javadoc for the several
// distinct situations it deliberately collapses into one opaque code, same "don't leak more than
// the generic shape" precedent as friends.ts's own not_found).
//
// Task 16 fix (owner directive, Decision #10): `profile.ProfileDto` deliberately never exposes
// account ids to the client, so a "profile" report addresses its subject by `subjectHandle`
// instead — server: `ModerationService#resolveSubjectId` resolves it internally via V10's
// case-insensitive unique index (the same lookup gifts/friends already use for handle-addressed
// actions), and an unresolvable handle degrades to the identical opaque not_found above, never a
// distinct oracle. Every other kind (look/message/guild) is unaffected and still sends a raw
// `subjectId`.
//
// Task 19 fix (owner directive, mirrors the Task 16 fix above): neither `StallDto`/
// `StallSummaryDto` (stalls.ts) expose a stall's internal numeric id — only the opaque `code` — so
// a "stall" report addresses its subject by `subjectCode` instead, resolved server-side the same
// way (`ModerationService#resolveSubjectId`, a plain lookup against `storefronts.code`); an
// unresolvable code degrades to the same opaque not_found, no new oracle.
export const submitReport = (input: SubmitReportInput) =>
  apiFetch<void>('/reports', { method: 'POST', body: JSON.stringify(input) });

/** No idempotencyKey (moves no value) and NOT cache-invalidating anything — a report doesn't
 *  change any data the reporting account can itself read back (the moderation queue it lands in
 *  is a separate, flag-gated moderator surface, out of this module's scope). */
export function useSubmitReport() {
  return useMutation({ mutationFn: submitReport });
}

// Phase 6 Task 19: server: moderation/AppealController.java — `POST /appeals {subjectKind,
// subjectId}`, void response, NOT flag-gated (`AppealController`'s own javadoc: "an owner can
// always appeal their own hidden content regardless of whether the moderator surface is currently
// enabled"). Unlike `submitReport`, this has no `subjectHandle` alternative — `AppealController`'s
// own `AppealRequest` record only ever carries `subjectId` (verified against the real source; the
// Task 16 fix's `subjectHandle` addition landed on `ReportRequest` only). This means an "appeal my
// own profile" caller needs its OWN account UUID as `subjectId` — available from `AccountView.id`
// (`api/auth.ts`'s `useQuery(['me'])`), never invented client-side. Refusals (ModerationService
// #appeal): `not_found` (ModerationSubjectNotFoundException — wrong owner or unknown subject,
// opaque, same as everywhere else in this class) and a distinct `not_hidden`
// (ModerationNotHiddenException, mapped by GlobalExceptionHandler — appealing content that isn't
// currently hidden has nothing to resolve).
export type AppealInput = { subjectKind: ReportSubjectKind; subjectId: string };

export const submitAppeal = (input: AppealInput) =>
  apiFetch<void>('/appeals', { method: 'POST', body: JSON.stringify(input) });

/** No idempotencyKey (moves no value — a pure state transition on `moderation_state.appeal_state`).
 *  Doesn't invalidate anything itself (same "screen composes its own domain invalidation" pattern
 *  `guilds.ts`'s bank mutations already document) — a screen showing an owner's own
 *  `moderationNotice.appealState` invalidates ITS OWN query (profile/look list/guild directory/
 *  stall) on success so the "Appeal requested" state renders immediately. */
export function useAppeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitAppeal,
    onSuccess: () => {
      // Broad, cheap invalidation of every surface that can render an owner-facing moderation
      // notice — appeals are rare (at most one per hidden subject), so there's no real cost to
      // invalidating all four rather than requiring every call site to know which one it's on.
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['looks'] });
      queryClient.invalidateQueries({ queryKey: ['guilds'] });
      queryClient.invalidateQueries({ queryKey: ['stalls'] });
    },
  });
}
